import React, { useEffect, useRef, useState } from 'react';
import identity from 'lodash/identity';
import uniqueId from 'lodash/uniqueId';
import Dropzone from 'react-dropzone';
import PreviewModal from '../../../../components/modals/PreviewModal';
import BaseModal from '../../../../components/modals/BaseModal';
import { MAX_VALID_FILE_SIZE, MAX_VALID_FILE_NAME_LENGTH } from '../../../../utils/Constants.js';
import BusyCover from '../../../../components/controls/BusyCover/BusyCover';
import { apiPOST, apiDELETE } from '../../../../utils/networking';
import { getFileNameWithoutExtension, } from '../../../../utils/fileUtils';
import FormControlError from '../../../../components/controls/Form/components/FormControlError';
import Label from '../../../../components/controls/Form/components/Label';
import Button from '../../../../components/controls/Button';

import './UploadDocument.less';

const invalidTypeMessage =
  'Unable to upload file. This file format is unsupported.' +
  ' Please upload a file format of Microsoft Word (.doc or .docx), Portable Document Format (.pdf), Rich Text (.rtf), or Plain Text (.txt).';
const invalidSizeMessage = 'File size should be less than 15 MB.';
const invalidNameLengthMessage = 'File name should be less than 95 characters.';
const generalMessage = 'Unknown error while uploading file. Please try again';

/**
 *
 * @param {File} file
 * @returns {boolean}
 */
const fileTypeValidation = file => {
  const types = [
    { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' },
    { type: 'text/plain' },
    { type: 'txt' },
    { type: 'application/pdf' },
    { type: 'application/msword' },
    { type: 'application/msword' },
    { type: 'text' },
    { type: 'doc' },
    { type: 'docx' },
    { type: 'pdf' },
    { type: 'application/rtf' },
  ];
  const validType = !!types.find(allowedType => allowedType.type === file.type);
  return validType;
};

/**
 *
 * @param {File} file
 * @returns {boolean}
 */
const fileSizeValidation = file => {
  return file.size < MAX_VALID_FILE_SIZE;
};

/**
 *
 * @param {File} file
 * @returns {boolean}
 */
const fileNameLengthValidation = file => {
  return getFileNameWithoutExtension(file.name).length <= MAX_VALID_FILE_NAME_LENGTH;
};

const uploadDocStyles = {
  width: '100%',
  height: '200px',
  borderWidth: '2px',
  borderColor: 'var(--main-primary-color)',
  borderStyle: 'dashed',
  borderRadius: '5px',
};
const activeStyleObj = {
  backgroundColor: 'color-mix(in srgb, var(--main-primary-color) 10%, var(--main-white))',
};

/**
 *
 * @param {{
 *  id?:string;
 *  ariaDescribedBy?:string;
 *  ariaLabel?:string;
 *  uploadEndpoint: string;
 *  deleteEndpoint: string;
 *  previewUrl: string|null;
 *  required?:boolean
 *  value:import('../../../../store/reducers/dashboardReducer').TUploadedFile|null;
 *  onChange?(value?:import('../../../../store/reducers/dashboardReducer').TUploadedFile|null):void;
 *  onUploadResponse?(response?:import('../../../../store/reducers/dashboardReducer').TUploadedFile):void;
 *  requireConfirmation?:boolean;
 * }} props
 * @returns {React.ReactElement}
 */
export const UploadDocument = (props) => {
  const {
    uploadEndpoint,
    deleteEndpoint,
    previewUrl,
    requireConfirmation,
    ariaDescribedBy,
    ariaLabel,
    id,
    value,
    onChange,
    onUploadResponse,
  } = props;

  const [controlUID] = useState(id ?? uniqueId('lsn_control_'));
  const [openPreviewModal, set_openDocumentModal] = useState(false);
  const [openErrorModal, set_openErrorModal] = useState(false);
  const [lastUploadedFileSize, set_lastUploadedFileSize] = useState('');
  const [loading, set_loading] = useState(false);
  const [required] = useState(props.required ?? false);
  const [uploadErrorMessage, set_uploadErrorMessage] = useState('',);
  const [isPreviewMode, set_isPreviewMode] = useState(false);
  const [isValid, set_isValid] = useState(true);
  const [isDirty, set_isDirty] = useState(false);
  const [lastUploadResult, set_lastUploadResult] = useState((/** @type {import('../../../../store/reducers/dashboardReducer').TUploadedFile=} */(void 0)));
  const isFetchingFileSizeRef = useRef(false);


  const fetchUploadedFileSize = async (/** @type {string} */ url) => {
    isFetchingFileSizeRef.current = true;
    const response = await fetch(url, { method: 'HEAD' });
    const cl = parseInt(response.headers.get("content-length") ?? '0')
    set_lastUploadedFileSize((cl / 1024).toFixed(2));
    isFetchingFileSizeRef.current = false;
  }

  const removeUploadDocument = async () => {
    set_loading(true);
    set_uploadErrorMessage('');

    try {
      await apiDELETE(deleteEndpoint);
      onUploadResponse?.();
      onChange?.();
    } catch (error) {
      set_uploadErrorMessage('Error in deleting file. Please try again.');
    }
    finally {
      set_loading(false);
    }
  };

  /**
   *
   * @param {Boolean} confirmUpload
   */
  const onDocumentModalClosed = confirmUpload => {
    // just close the modal when it was just preview
    if (isPreviewMode) {
      set_openDocumentModal(false);
      set_isPreviewMode(false);
      return;
    }
    set_openDocumentModal(false);
    set_isPreviewMode(false);
    confirmUpload ? onChange?.(lastUploadResult) : removeUploadDocument();
    setTimeout(() => {
      confirmUpload ? document.getElementById(`${controlUID}_uploaded_doc_view_link`)?.focus() : void 0;
    });
  };

  const showDocumentPreview = () => {
    set_openDocumentModal(true);
    set_isPreviewMode(true);
  };

  /**
   *
   * @param {FormData} formData
   */
  const handleFileUploadSubmit = async (formData) => {
    set_uploadErrorMessage('');
    set_loading(true);

    const file = (/** @type {File} */(formData.get('file')));
    const fileSize = file.size / 1024;

    try {
      const responseUpload = await apiPOST(uploadEndpoint, formData);
      set_lastUploadedFileSize(fileSize.toFixed(2));
      const uploadResult = {
        id: responseUpload.body.id,
        fileName: responseUpload.body.fileName,
        fileUploadedDate: responseUpload.body.uploadDate,
      };

      set_lastUploadResult(uploadResult);
      onUploadResponse?.(uploadResult);

      // show the preview for confirmation
      requireConfirmation ? set_openDocumentModal(true) : onChange?.(uploadResult);
    } catch {
      set_openErrorModal(true);
    } finally {
      set_loading(false);
    }
  };

  useEffect(() => {
    set_isValid(required ? !!value : true);
    previewUrl && !lastUploadedFileSize && !isFetchingFileSizeRef.current ? fetchUploadedFileSize(previewUrl) : void 0;
  }, [value, previewUrl]);

  return (
    <div className={["lsn-upload", isDirty && !isValid ? 'has-error' : ''].filter(identity).join(' ')}>

      <Label className='pl-2 ml-2 mt-2 d-block' id={`${controlUID}_upload_notes`}>
        <b>Note:</b> the file in Microsoft Word (.doc or .docx), Portable Document
        Format (.pdf), Rich Text Format (.rtf), or Plain Text File (.txt). File size should be less than 15 MB. File
        name should be less than 95 characters.
      </Label>

      {uploadErrorMessage ? (
        <p className="lsn-input-error" role={`alert`}>
          {uploadErrorMessage}
        </p>
      ) : (
        void 0
      )}

      {value?.fileName ? (
        <div className="lsn-upload-display-container">
          <BusyCover isBusy={loading} />
          <i className='far fa-file-alt color-primary' aria-hidden></i>
          <div className="lsn-upload-display-details p-1">
            <Button
              id={`${controlUID}_uploaded_doc_view_link`}
              primary
              link
              tabIndex={0}
              className="lsn-upload-view-attachment"
              onKeyPress={showDocumentPreview}
              onClick={showDocumentPreview}
              title={`Click to preview the uploaded document ${value.fileName}.`}
              aria-label={`Click to preview the uploaded document ${value.fileName}.`}
              aria-describedby={`${controlUID}_uploaded_details`}
            >
              {value.fileName.length > 40
                ? [value.fileName.substring(0, 30), value.fileName.slice(-10)].join('...')
                : value.fileName}
            </Button>

            <p id={`${controlUID}_uploaded_details`}>
              {lastUploadedFileSize ? <span aria-label={`Uploaded file size is ${lastUploadedFileSize}kb.`}>{lastUploadedFileSize}kb</span> : ''}
              {value.fileUploadedDate ? <span aria-label={`Uploaded on ${value.fileUploadedDate}.`}>{value.fileUploadedDate}</span> : ''}
            </p>
            <Button
              danger
              link
              className="lsn-remove-link"
              tabIndex={0}
              onKeyPress={removeUploadDocument}
              onClick={removeUploadDocument}
              title={`Remove this uploaded document ${value.fileName}.`}
              aria-label={`Remove this uploaded document ${value.fileName}.`}
            >
              Remove
            </Button>
          </div>
        </div>
      ) : (
        <>
          <Dropzone
            onFileDialogOpen={() => {
              set_isDirty(true);
              set_uploadErrorMessage('');
              set_loading(false);
            }}
            onDrop={accepted => {
              const formData = accepted.reduce((fData, file) => {
                const isValidType = fileTypeValidation(file);
                const isValidSize = fileSizeValidation(file);
                const isNameLengthValid = fileNameLengthValidation(file);
                isValidType && isValidSize && isNameLengthValid
                  ? fData.append('file', file)
                  : fData.set('isInvalid', '');
                !isValidType ? fData.set('isInvalidType', '') : void 0;
                !isValidSize ? fData.set('isInvalidSize', '') : void 0;
                !isNameLengthValid ? fData.set('isInvalidNameLength', '') : void 0;
                return fData;
              }, new FormData());

              const setErrorMessage = () => {
                if (formData.has('isInvalidType')) {
                  return invalidTypeMessage;
                } else if (formData.has('isInvalidSize')) {
                  return invalidSizeMessage;
                } else if (formData.has('isInvalidNameLength')) {
                  return invalidNameLengthMessage;
                } else {
                  return generalMessage;
                }
              };
              formData.has('isInvalid')
                ? (() => { set_uploadErrorMessage(setErrorMessage()); set_loading(false); })()
                : handleFileUploadSubmit(formData);
            }}
          >
            {({ getRootProps, getInputProps, isDragActive, isDragAccept }) => {
              return (
                <div
                  {...getRootProps({
                    style: {
                      ...uploadDocStyles,
                      ...(isDragActive || isDragAccept ? activeStyleObj : {}),
                    },
                  })}
                  aria-label={ariaLabel}
                  aria-describedby={ariaDescribedBy}
                  onFocus={() => set_isDirty(true)}
                  onBlur={() => {
                    set_isDirty(true);
                  }}
                  className="lsn-dropzone-overlay"
                >
                  <BusyCover isBusy={loading} hasError={isDirty && !isValid} />
                  <input {...getInputProps()}
                    tabIndex={-1}
                    id={controlUID}
                    style={{ display: 'block', height: '1px', opacity: 0 }}
                    required={required}
                    aria-hidden
                    onInvalid={() => {
                      set_isDirty(true);
                    }}
                  />
                  <p className="text-center lsn-document-instructions">Drop files here to attach</p>
                  <p className="text-center">or</p>
                  <div className="text-center lsn-upload-link">
                    <a
                      tabIndex={0}
                      role="button"
                      aria-describedby={`${controlUID}_upload_notes`}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          e.stopPropagation();
                          // @ts-ignore
                          document.querySelectorAll('.lsn-upload-link').forEach(node => node.click());
                        }
                      }}
                      title={'Click to browse'}
                    >
                      <i className='far fa-upload color-primary'></i>{' '}
                      Click to browse
                    </a>
                  </div>
                </div>
              );
            }}
          </Dropzone>
          <FormControlError show={isDirty && !isValid} text={required ? 'Please upload a file' : ''} />
        </>
      )}

      <PreviewModal
        show={openPreviewModal}
        title={'Document Preview'}
        subtitle={'Please review the document below:'}
        body={isPreviewMode ? '' : 'Does this document look correct?'}
        documentURL={previewUrl}
        buttonContinue={isPreviewMode ? 'Cancel' : 'Yes, Upload It'}
        buttonCancel={isPreviewMode ? '' : 'No, Cancel Upload'}
        onClose={onDocumentModalClosed}
      ></PreviewModal>

      <BaseModal
        size='small'
        show={openErrorModal}
        title={'Upload Unsuccessful'}
        body={
          'We were not able to complete the upload. Please try uploading your file again. If this issue persists, please contact support.'
        }
        modalPrimaryButton={'OK'}
        onClose={() => set_openErrorModal(false)}
      ></BaseModal>
    </div >
  );
}

export default UploadDocument;
